<script setup lang="ts">
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import useDepartmentCostCenters from "@/composables/useDepartment.ts";
import useCompanyCostCenter from "@/modules/companies/composables/useCompanyCostCenter.ts";
import useCompanyDepartment from "@/modules/companies/composables/useCompanyDepartment.ts";
import { useCompanyGroups } from "@/modules/groups/composables/useCompanyGroups.ts";
import usePendingRequest from "@/modules/pendingRequests/composables/usePendingRequest.ts";
import { NewDataUser } from "@/modules/pendingRequests/interfaces/pendingRequest.ts";
import useAuthUser from "@/composables/useAuthUser";
import useRoles from "@/modules/roles/composables/useRoles.ts";
import useUser from "@/modules/users/composables/useUser.ts";
import useUserRole from "@/modules/users/composables/useUserRole.ts";
import useUsersSearch from "@/modules/users/composables/useUsersSearch.ts";
import {
  actorRank,
  roleRank,
} from "@/modules/users/helpers/roleHierarchy";
import { User } from "@/modules/users/interfaces/user.ts";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

interface Props {
  companyId: string | number;
  approvalId: number;
  approvalState: string;
}

const props = defineProps<Props>();
const route = useRoute();

enum RoleFilter {
  Master = "Master",
  Socio = "Socio",
}

const toast = useToast();
const { user: actorUser } = useAuthUser();

const { approveMutationAsync, rejectedMutationAsync, approvalRefetch } =
  usePendingRequest<NewDataUser>({
    params: {
      id: route.params.id as string,
    },
    immediate: true,
  });

const supervisorFilter = ref<{ company?: number }>({
  company: undefined,
});

const { updateUser } = useUser({
  id: route.params.id as string,
  immediate: false,
});

const { updateRole } = useUserRole({
  immediate: false,
  params: {
    id: route.params.id as string,
  },
});

type Form = {
  departmentId: number | undefined;
  roleId: number | undefined;
  groupId: number | undefined;
  supervisorId: User | undefined;
  costCenterId: number | undefined;
};

const form = reactive<Form>({
  departmentId: undefined,
  roleId: undefined,
  groupId: undefined,
  supervisorId: undefined,
  costCenterId: undefined,
});

// Motivo del rechazo (obligatorio): el backend lo exige para approvals de usuario.
const rejectReason = ref("");
const rejectReasonError = ref("");

const {
  data: roles,
  refetch: refetchRoles,
  setCompanyId,
  isLoading,
} = useRoles({
  immediate: false,
});

const {
  data: groups,
  setCompany,
  refetch: refetchGroups,
  isLoading: loadingGroups,
} = useCompanyGroups({
  immediate: false,
});

const {
  departments,
  refetch: refetchDepartment,
  setCompanyId: setCompanyDep,
  isLoading: loadingDep,
} = useCompanyDepartment({
  immediate: false,
  params: {},
});

const {
  costCenters,
  refetch: refetchCostCenter,
  isLoading: loadingCost,
  departameniId,
} = useDepartmentCostCenters({
  immediate: false,
  params: {
    id: form.departmentId!,
  },
});

const {
  users,
  setQuery: setUserQuery,
  isFetching: fetchingUsers,
  refetch,
} = useUsersSearch({
  automaticSearch: true,
  params: {
    filters: supervisorFilter,
  },
  immediate: false,
});

watch(
  () => props.companyId,
  (value) => {
    if (value) {
      setCompanyId(Number(value));
      refetchRoles();

      setCompany(value.toString());
      refetchGroups();

      setCompanyDep(Number(value));
      refetchDepartment();

      supervisorFilter.value.company = Number(value)!;
      refetch();
    }
  },
  {
    immediate: true,
  },
);

onMounted(() => {
  if (props.companyId) {
    setCompanyId(Number(props.companyId));
    refetchRoles();

    setCompany(props.companyId.toString());
    refetchGroups();

    setCompanyDep(Number(props.companyId));
    refetchDepartment();

    supervisorFilter.value.company = Number(props.companyId)!;
    refetch();
  }
});

watch(
  () => form.departmentId,
  (value) => {
    if (value) {
      departameniId.value = value;
      refetchCostCenter();
    }
  },
  {
    immediate: true,
  },
);

const { createCompanyCostCenterAsync, createCostCenterLoading } =
  useCompanyCostCenter();

const modal = ref(false);

const openModal = () => {
  modal.value = true;
};

const closeModal = () => {
  modal.value = false;
};

const roleFilter = computed(() => {
  const actorRoles = actorUser.value?.roles ?? [];
  // Rango derivado de ASSIGNABLE_ROLES (helper), no una tabla numérica local.
  const rank = actorRank(actorUser.value);
  const actorLevel = rank ?? Infinity;
  const isSupervisor = actorRoles.includes("Supervisor");

  return (roles?.value ?? [])
    .filter((role) => {
      if (!isFinite(actorLevel)) return false;
      const level = roleRank(role.name);
      if (level === -1) return false;
      // OJO: para Supervisor es ESTRICTO (`>`), no `>=` como en las otras
      // pantallas. No es un descuido: esta pantalla aprueba ALTAS, y el backend
      // valida esas con StoreUserRequest, que excluye el propio nivel del actor
      // — distinto de RoleHierarchy::ASSIGNABLE, que lo incluye y gobierna el
      // CAMBIO de rol. Igualar los operadores aquí produciría 422.
      return isSupervisor ? level > actorLevel : level >= actorLevel;
    })
    .map((role) => ({ label: role.name, value: role.id }));
});

const handleSubmit = async (idApproval: number) => {
  const data = await approveMutationAsync({
    params: {
      id: idApproval,
    },
    config: {
      raiseToast: true,
      onSuccessToast: "La solicitud se aprobo con exito.",
      onErrorToast: "No se pudo aprobar la solicitud",
    },
  });

  // El approve de un User approval responde con UserResource + additional(message):
  // additional fuerza el wrapper `data` aunque withoutWrapping esté activo, así que el
  // usuario creado vive en data.data, no en la raíz. Sin esto, id => undefined y
  // updateUser/updateRole pegaban a /api/users/undefined (500 "id = undefined").
  const approvedUser = (data as any)?.data ?? data;
  const userId = approvedUser?.id;

  if (userId) {
    // updateUser es el .mutate() fire-and-forget: useUser.ts ya no traga su
    // error con un toast genérico, así que sin este onError un fallo aquí
    // quedaba completamente silencioso.
    updateUser(
      {
        departmentId: form.departmentId,
        costCenterId: form.costCenterId!,
        companyGroupId: form.groupId,
        supervisorId: form.supervisorId?.id,
        id: userId,
      },
      {
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message ||
              "No se pudo completar la asignación del usuario aprobado"
          );
        },
      }
    );

    await updateRole({
      params: {
        id: userId,
        roles: [form.roleId!],
      },
      config: {
        raiseToast: false,
      },
    });
  }

  approvalRefetch();
};

const rejectApproval = async (idApproval: number) => {
  if (!rejectReason.value.trim()) {
    rejectReasonError.value = "Escribe el motivo del rechazo.";
    return;
  }

  await rejectedMutationAsync({
    params: {
      id: idApproval,
      comment: rejectReason.value.trim(),
    },
    config: {
      raiseToast: true,
      onSuccessToast: "La solicitud se rechazo",
      onErrorToast: "No se pudo rechazar la solicitud",
    },
  });

  approvalRefetch();
};

const formCostCenter = reactive({
  departmentId: "",
  name: "",
  key: "",
});

const rules = {
  departmentId: {
    required: helpers.withMessage("El departamento es requerido", required),
  },
  key: {
    required: helpers.withMessage(
      "La llave de centro de costos es requerido",
      required,
    ),
  },
  name: {
    required: helpers.withMessage(
      "El nombre del centro de costos es requerido",
      required,
    ),
  },
};

const v$ = useVuelidate(rules, formCostCenter);

const handleCreateCostCenter = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    } else {
      for (const data of field.$response.$data) {
        for (const key in data) {
          if (field.$response.$errors[0][key].length >= 1) {
            toast.error(field.$response.$errors[0][key][0].$message);
          }
        }
      }
    }
  }

  try {
    await createCompanyCostCenterAsync({
      params: {
        ...formCostCenter,
        departmentId: Number(formCostCenter.departmentId),
        companyId: Number(props.companyId),
      },
      config: {
        raiseToast: true,
        onSuccessToast: "Centro de costos creado con éxito",
        onErrorToast: "Error al crear departamento",
      },
    });

    closeModal();
    await refetchCostCenter();
  } catch (error: any) {
    toast.error(error?.response?.data?.message);
  } finally {
    formCostCenter.departmentId = "";
    formCostCenter.name = "";
    formCostCenter.key = "";
  }
};
</script>

<template>
  <form
    class=""
    autocomplete="off"
    v-if="approvalState !== 'approved' && approvalState !== 'rejected'"
  >
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <StyledSelect
        v-model="form.departmentId"
        label="Departamento"
        :options="
          departments?.map((department) => ({
            label: department.name,
            value: department.id,
          }))
        "
        is-required
        :loading-options="loadingDep"
        :disabled="loadingDep"
      />

      <div class="flex gap-2" v-if="form.departmentId">
        <div class="w-full">
          <StyledSelect
            v-model="form.costCenterId"
            :options="
              costCenters?.map((costCenter) => ({
                label: costCenter.name,
                value: costCenter.id,
              })) || []
            "
            label="Centro de costos"
            is-required
            :loading-options="loadingCost"
            :disabled="loadingCost"
          />
        </div>

        <StyledButton
          type="button"
          class="bg-green-400 text-white hover:bg-green-500 mt-7"
          v-tooltip="'Crear centro de costos'"
          @click="openModal()"
        >
          <span class="material-symbols-outlined"> add </span>
        </StyledButton>
      </div>

      <StyledSelect
        label="Rol"
        v-model="form.roleId"
        :options="roleFilter"
        is-required
        :loading-options="isLoading"
        :disabled="isLoading"
      />

      <StyledSelect
        label="Grupos de viajes"
        v-model="form.groupId"
        :options="
          groups?.map((group) => ({
            label: group.name,
            value: group.id,
          })) || []
        "
        :loading-options="loadingGroups"
        :disabled="loadingGroups"
        is-required
      />

      <StyledCombobox
        class="mt-2"
        v-model="form.supervisorId"
        label="Supervisor"
        placeholder="Buscar supervisor"
        :options="users"
        :loading-options="fetchingUsers"
        :display-value="
          (option) => `${option.name} ${option.lastname} (${option.email})`
        "
        :on-input-change="(value) => setUserQuery(value)"
        is-required
      >
        <template #default="{ option }">
          <span>{{
            `${option.name} ${option.lastname} (${option.email} `
          }}</span>
        </template>
      </StyledCombobox>
    </div>

    <div class="flex justify-end gap-2 mt-5">
      <StyledButton
        label="Aprobar solicitud"
        @click="handleSubmit(approvalId)"
        type="button"
        class="text-white"
        :disabled="
          !form.departmentId ||
          !form.costCenterId ||
          !form.roleId ||
          !form.supervisorId
        "
        :class="[
          !form.departmentId || !form.roleId || !form.supervisorId
            ? 'cursor-not-allowed bg-green-300 hover:bg-green-300'
            : 'bg-green-500 hover:bg-green-700',
        ]"
      />

      <StyledButton
        label="Denegar Solicitud"
        @click="rejectApproval(approvalId)"
        type="button"
        class="bg-red-500 text-white hover:bg-red-700"
      />
    </div>

    <div class="flex flex-col gap-1.5 mt-3">
      <label for="reject-reason" class="text-sm font-medium text-gray-700">
        Motivo del rechazo
      </label>
      <textarea
        id="reject-reason"
        v-model="rejectReason"
        rows="3"
        placeholder="Explica por qué se rechaza esta solicitud"
        class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 resize-none"
        @input="rejectReasonError = ''"
      />
      <p v-if="rejectReasonError" class="text-xs text-red-600">
        {{ rejectReasonError }}
      </p>
    </div>
  </form>

  <ModalComponent :model-value="modal" @update:model-value="closeModal()">
    <template #modalBody>
      <ShadowBox>
        <h3 class="text-lg font-bold">Crear centro de costos</h3>

        <form
          class="flex flex-col gap-3 mt-4"
          @submit.prevent="handleCreateCostCenter"
          autocomplete="off"
        >
          <StyledSelect
            label="Departamento"
            class-label="font-medium text-base"
            v-model="formCostCenter.departmentId"
            :options="
              departments?.map((department) => ({
                label: department.name,
                value: department.id,
              })) || []
            "
          />

          <StyledInput
            label="Llave del centro de costos"
            placeholder="Ej. 816"
            v-model="formCostCenter.key"
          />

          <StyledInput
            label="Nombre del centro de costos"
            placeholder="Ej. Administración"
            name="name"
            v-model="formCostCenter.name"
          />

          <div class="flex justify-between mt-2">
            <StyledButton
              type="button"
              label="Volver"
              class="bg-blue-700 text-white"
              @click="closeModal()"
            />
            <StyledButton
              type="submit"
              label="Crear centro de costos"
              class="bg-[#253C70] text-white"
              :loading="createCostCenterLoading"
            />
          </div>
        </form>
      </ShadowBox>
    </template>
  </ModalComponent>
</template>

<style scoped></style>
